/**
 * Token manager for automatic token refresh
 */

/**
 * JWT token payload interface
 */
interface JwtPayload {
  exp: number; // Expiration timestamp (seconds)
  [key: string]: any;
}

/**
 * Parse JWT token to extract payload
 * Note: This does NOT verify the token signature, only decodes the payload
 */
export function parseJwt(token: string): JwtPayload | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) {
      return null;
    }

    const base64Url = parts[1] || "";
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");

    // Decode base64 in browser environment
    if (typeof atob !== "undefined") {
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join("")
      );
      return JSON.parse(jsonPayload);
    }

    // Fallback for Node.js environment
    if (typeof Buffer !== "undefined") {
      const jsonPayload = Buffer.from(base64, "base64").toString("utf-8");
      return JSON.parse(jsonPayload);
    }

    return null;
  } catch (error) {
    console.error("[AuthClient] Failed to parse JWT token:", error);
    return null;
  }
}

/**
 * Token manager class for handling automatic token refresh
 */
export class TokenManager {
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private isRefreshing = false;
  private refreshCallback: (() => Promise<void>) | null = null;

  /**
   * Set the refresh callback function
   */
  setRefreshCallback(callback: () => Promise<void>): void {
    this.refreshCallback = callback;
  }

  /**
   * Schedule a token refresh after a delay
   * @param delayMs Delay in milliseconds before refreshing
   */
  scheduleRefresh(delayMs: number): void {
    // Clear existing timer
    this.clearSchedule();

    // Don't schedule if delay is negative or zero
    if (delayMs <= 0) {
      return;
    }

    this.refreshTimer = setTimeout(() => {
      this.refresh();
    }, delayMs);
  }

  /**
   * Schedule token refresh based on token expiration and threshold
   * @param accessToken JWT access token
   * @param thresholdSeconds Refresh threshold in seconds (default: 300)
   */
  scheduleRefreshFromToken(accessToken: string, thresholdSeconds: number = 300): void {
    const payload = parseJwt(accessToken);
    if (!payload || !payload.exp) {
      console.warn("[AuthClient] Cannot schedule refresh: invalid token or missing exp claim");
      return;
    }

    const expiresAtMs = payload.exp * 1000; // Convert to milliseconds
    const nowMs = Date.now();
    const expiresInMs = expiresAtMs - nowMs;

    // Calculate when to refresh (before expiration by thresholdSeconds)
    const refreshDelayMs = expiresInMs - thresholdSeconds * 1000;

    if (refreshDelayMs <= 0) {
      // Token already expired or will expire very soon
      console.warn("[AuthClient] Token already expired or expiring soon, refreshing immediately");
      this.refresh();
    } else {
      this.scheduleRefresh(refreshDelayMs);
    }
  }

  /**
   * Manually trigger token refresh
   */
  async refresh(): Promise<void> {
    if (this.isRefreshing) {
      return;
    }

    if (!this.refreshCallback) {
      console.error("[AuthClient] No refresh callback set");
      return;
    }

    this.isRefreshing = true;

    try {
      await this.refreshCallback();
    } catch (error) {
      console.error("[AuthClient] Token refresh failed:", error);
    } finally {
      this.isRefreshing = false;
    }
  }

  /**
   * Clear the scheduled refresh timer
   */
  clearSchedule(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  /**
   * Check if currently refreshing
   */
  isCurrentlyRefreshing(): boolean {
    return this.isRefreshing;
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    this.clearSchedule();
    this.refreshCallback = null;
    this.isRefreshing = false;
  }
}
